import { logger } from "../utils/logger";
import { apiClient } from "./axiosInstance";
import { tripRegistryClient } from "./tripRegistryClient";

/**
 * Attach auth interceptors (request + response) to a given axios instance.
 * @param {import('axios').AxiosInstance} client
 * @param {Function} getAccessTokenSilently
 * @param {object} ids - object to store/eject interceptor IDs
 */
function attachInterceptors(client, getAccessTokenSilently, ids) {
  if (ids.requestId !== undefined) {
    client.interceptors.request.eject(ids.requestId);
  }
  if (ids.responseId !== undefined) {
    client.interceptors.response.eject(ids.responseId);
  }

  ids.requestId = client.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessTokenSilently();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          logger.warn("No access token available, blocking request");
          return Promise.reject(
            new Error("Authentication required - no token available")
          );
        }
      } catch (error) {
        logger.error("Failed to get access token:", error);
        return Promise.reject(error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  ids.responseId = client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const token = await getAccessTokenSilently({ cacheMode: "off" });
          if (!token) throw new Error("No token received from Auth0");
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return client(originalRequest);
        } catch (refreshError) {
          logger.error("Token refresh failed:", refreshError);
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Setup Axios interceptors to handle authentication
 * - Request interceptor: Proactively adds fresh token to every request
 * - Response interceptor: Handles 401 errors by forcing token refresh
 * @param {Function} getAccessTokenSilently - Auth0 function to get a fresh token
 */
export function setupAxiosAuthInterceptor(getAccessTokenSilently) {
  attachInterceptors(
    apiClient,
    getAccessTokenSilently,
    setupAxiosAuthInterceptor
  );
  attachInterceptors(
    tripRegistryClient,
    getAccessTokenSilently,
    setupAxiosAuthInterceptor.tripRegistry ||
      (setupAxiosAuthInterceptor.tripRegistry = {})
  );
}
